import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import authService, { AuthResponse, LoginAccount } from "../services/authService";
import profileService from "../services/profileService";
import TokenExpirationService from "../services/tokenExpirationService";

export interface User {
  id: string;
  phoneNumber: string;
  name?: string;
  email?: string;
  companyName: string;
  companyAddress: string;
  personName: string;
  mailId: string;
  role: "auctioneer" | "participant" | "admin" | "channel_partner" | "auction_viewer";
  isVerified: boolean;
  createdAt?: string;
gstn_number: string;
  company_product_service: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  token: string | null;
  login: (email: string) => Promise<any>; // Return response
  verifyOTP: (otp: string) => Promise<VerifyOTPResult>;
  selectAccount: (userId: number | string) => Promise<void>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  syncProfile: () => Promise<void>;
}

// Several accounts can share one email: the OTP is verified first and the
// user then picks which account to enter.
export interface VerifyOTPResult {
  requiresAccountSelection: boolean;
  accounts?: LoginAccount[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [expirationTimer, setExpirationTimer] = useState<NodeJS.Timeout | null>(null);

  // Merge user helper
  const mergeUserData = (backendUser: any, fallbackUser: User | null = null): User => {
    const base = fallbackUser || user;


const companyProductService = 
    backendUser.company_product_service ||
    backendUser.companyproductservice ||
    backendUser.company_product_services || // Add more possible variations
    base?.company_product_service ||
    "";


    return {
      id: backendUser.id || base?.id || Date.now().toString(),
      phoneNumber: backendUser.phoneNumber || backendUser.phone_number || base?.phoneNumber || "",
      name: backendUser.name || backendUser.personName || base?.name,
      email: backendUser.email || backendUser.mailId || base?.email,
      companyName: backendUser.companyName || backendUser.company_name || base?.companyName || "",
      companyAddress: backendUser.companyAddress || backendUser.company_address || base?.companyAddress || "",
      personName: backendUser.personName || backendUser.person_name || base?.personName || "",
      mailId: backendUser.mailId || backendUser.email || base?.mailId || "",
      role: (backendUser.role || base?.role || "participant") as User["role"],
      isVerified: backendUser.isVerified ?? base?.isVerified ?? true,
      createdAt: backendUser.createdAt || backendUser.created_at || base?.createdAt || new Date().toISOString(),
gstn_number:
        backendUser.gstn_number ||
        backendUser.gstnumber ||
        base?.gstn_number ||
        "",
 company_product_service: companyProductService, // Fixed: Use the properly mapped value
    };
  };

  const syncProfile = async (): Promise<void> => {
    const storedToken = token || localStorage.getItem("authToken");
    if (!storedToken) return;

    try {
      const res = await profileService.getProfile(storedToken);
      if (!res?.user) return;

      const currentUser = user || JSON.parse(localStorage.getItem("auctionUser") || "null");
      const mergedUser = mergeUserData(res.user, currentUser);
      setUser((prev) => {
        const changed = !prev || JSON.stringify(prev) !== JSON.stringify(mergedUser);
        if (changed) localStorage.setItem("auctionUser", JSON.stringify(mergedUser));
        return changed ? mergedUser : prev;
      });
    } catch (err) {
      console.warn("Profile sync failed:", err);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUserRaw = localStorage.getItem("auctionUser");
        const storedToken = localStorage.getItem("authToken");
        
        if (storedUserRaw && storedToken) {
          // Check if session has expired
          if (TokenExpirationService.isSessionExpired()) {
            logout();
          } else {
            setUser(JSON.parse(storedUserRaw));
            setToken(storedToken);
            
            // Set up expiration timer
            const timer = TokenExpirationService.setupExpirationTimer(() => {
              logout();
              alert("Your session has expired. Please login again.");
            });
            setExpirationTimer(timer);
          }
        }
      } catch (err) {
        console.warn("Auth init error:", err);
      } finally {
        setLoading(false);
      }
    };
    initializeAuth();

    // Cleanup timer on unmount
    return () => {
      if (expirationTimer) {
        clearTimeout(expirationTimer);
      }
    };
  }, []);

  const login = async (email: string): Promise<any> => {
    try {
      const cleanEmail = email.trim().toLowerCase();
      const response = await authService.sendOTP({ email: cleanEmail });

      if (!response.success) {
        console.error('[AuthContext] Response not successful:', response.message);
        throw new Error(response.message || "Failed to send OTP");
      }

      // Save pending email/session
      localStorage.setItem("pendingEmail", cleanEmail);
      if (response.maskedEmail) localStorage.setItem("pendingOtpEmail", response.maskedEmail);
      else localStorage.removeItem("pendingOtpEmail");
      if (response.sessionId) localStorage.setItem("sessionId", response.sessionId);

      return response; // ✅ Return response for LoginPage
    } catch (err: any) {
      console.error('[AuthContext] Error caught:', err);
      // Re-throw the original error message
      throw new Error(err?.message || "OTP request failed");
    }
  };

  // Stores the session once the server has settled on a single account
  const completeLogin = (response: AuthResponse) => {
    const userData = response.user ? mergeUserData(response.user) : null;
    if (userData) {
      setUser(userData);
      localStorage.setItem("auctionUser", JSON.stringify(userData));
      if (response.token) {
        setToken(response.token);
        localStorage.setItem("authToken", response.token);

        // Set login timestamp for 3-hour expiration tracking
        TokenExpirationService.setLoginTimestamp();

        // Clear any existing timer
        if (expirationTimer) {
          clearTimeout(expirationTimer);
        }

        // Set up new expiration timer
        const timer = TokenExpirationService.setupExpirationTimer(() => {
          logout();
          alert("Your session has expired. Please login again.");
        });
        setExpirationTimer(timer);
      }
    }

    clearPendingLogin();
  };

  const clearPendingLogin = () => {
    localStorage.removeItem("pendingEmail");
    localStorage.removeItem("pendingOtpEmail");
    localStorage.removeItem("sessionId");
  };

  const verifyOTP = async (otp: string): Promise<VerifyOTPResult> => {
    try {
      const email = localStorage.getItem("pendingEmail");
      const sessionId = localStorage.getItem("sessionId");
      if (!email || !sessionId) throw new Error("Session expired, request OTP again");

      const response = await authService.verifyOTP({ sessionId, otp, email });
      if (!response.success) throw new Error(response.message || "OTP verification failed");

      if (response.requiresAccountSelection) {
        return { requiresAccountSelection: true, accounts: response.accounts || [] };
      }

      completeLogin(response);
      return { requiresAccountSelection: false };
    } catch (err) {
      console.error("OTP verify error:", err);
      throw err;
    }
  };

  const selectAccount = async (userId: number | string) => {
    const email = localStorage.getItem("pendingEmail");
    const sessionId = localStorage.getItem("sessionId");
    if (!email || !sessionId) throw new Error("Session expired, request OTP again");

    const response = await authService.selectAccount({ sessionId, email, userId });
    if (!response.success) throw new Error(response.message || "Could not select account");

    completeLogin(response);
  };

  const logout = () => {
    // Clear expiration timer
    if (expirationTimer) {
      clearTimeout(expirationTimer);
      setExpirationTimer(null);
    }
    
    setUser(null);
    setToken(null);
    localStorage.removeItem("auctionUser");
    localStorage.removeItem("authToken");
    localStorage.removeItem("pendingPhoneNumber");
    clearPendingLogin();
    
    // Clear login timestamp
    TokenExpirationService.clearLoginTimestamp();
  };

  const updateProfile = async (data: Partial<User>) => {
    if (!user) throw new Error("No user logged in");
    setLoading(true);
    try {
      const payload: any = {};
      if (data.phoneNumber) payload.phone_number = data.phoneNumber;
      if (data.name || data.personName) payload.person_name = data.name || data.personName;
      if (data.email || data.mailId) payload.email = data.email || data.mailId;
      if (data.companyName) payload.company_name = data.companyName;
      if (data.companyAddress) payload.company_address = data.companyAddress;

 if (data.gstn_number !== undefined)
          payload.gstn_number = data.gstn_number;
        if (data.company_product_service !== undefined)
          payload.company_product_service = data.company_product_service;




      const res = await profileService.updateProfile(user.id, payload);
      if (res.user) {
        const merged = mergeUserData(res.user, user);
        setUser(merged);
        localStorage.setItem("auctionUser", JSON.stringify(merged));
      }
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    await login(email);
  };

  const value: AuthContextType = {
    user,
    loading,
    token,
    login,
    verifyOTP,
    selectAccount,
    logout,
    updateProfile,
    forgotPassword,
    refreshToken: async () => {},
    syncProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};


